import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pill, Pencil, Trash2, Plus, Check, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PickupRows, toPickupPayload, MAX_PICKUPS } from "@/components/PickupRows";

const toRows = (list) =>
  list.length ? list.map((p) => ({ pharmacy_id: p.pharmacy_id, qty: p.pickup_qty != null ? String(p.pickup_qty) : "" })) : [{ pharmacy_id: "", qty: "" }];

function PickupLine({ p, idx, canRemove, onRemove, busy }) {
  const done = p.status === "picked_up";
  return (
    <div className="flex items-start justify-between gap-3" data-testid={`pickup-current-${idx}`}>
      <div className="min-w-0">
        <span className="font-display font-semibold text-sm" data-testid={`pickup-current-name-${idx}`}>
          {p.customer_name?.replace(/^Pickup · /, "") || "Pickup"}
        </span>
        {p.pickup_qty != null && (
          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium" data-testid={`pickup-current-qty-${idx}`}>
            {p.pickup_qty} pkg{p.pickup_qty === 1 ? "" : "s"}
          </span>
        )}
        {done && <span className="ml-2 text-[10px] font-medium text-emerald-700">Picked up</span>}
        <div className="text-[11px] text-muted-foreground mt-0.5">{p.address}</div>
      </div>
      {canRemove && !done && (
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => onRemove(p)} disabled={busy} data-testid={`pickup-remove-btn-${idx}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
        </Button>
      )}
    </div>
  );
}

// Add / change / remove up to 2 pickup stops on a built route via POST /routes/{id}/pickup.
export default function PickupEditor({ activeRoute, pharmacies, onSaved }) {
  const pickups = useMemo(() => (activeRoute?.stops || []).filter((s) => s.is_pickup), [activeRoute]);
  const pending = pickups.filter((p) => p.status !== "picked_up");
  const lockedIds = pickups.filter((p) => p.status === "picked_up").map((p) => p.pharmacy_id);
  const startPharmacyId = activeRoute?.start_pharmacy_id || null;
  const isCompleted = activeRoute?.status === "completed";
  const slots = MAX_PICKUPS - lockedIds.length;

  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(toRows(pending));

  const send = async (list, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/routes/${activeRoute.id}/pickup`, { pickups: list });
      toast.success(msg);
      setEditing(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const list = toPickupPayload(rows);
    if (!list.length) return toast.error("Pick a pharmacy first");
    send(list, "Pickups updated");
  };

  const remove = (p) => {
    if (!window.confirm("Remove this pickup stop from the route?")) return;
    const list = pending.filter((x) => x.pharmacy_id !== p.pharmacy_id).map((x) => ({ pharmacy_id: x.pharmacy_id, qty: x.pickup_qty ?? null }));
    send(list, "Pickup removed");
  };

  return (
    <Card className="p-4 rounded-xl border-border bg-card/80 mb-4" data-testid="pickup-editor">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0 bg-amber-100 text-amber-700">
          <Pill className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Pickup pharmacies <span className="normal-case tracking-normal">({pickups.length}/{MAX_PICKUPS})</span>
            </div>
            {!isCompleted && !editing && slots > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary"
                onClick={() => { setRows(toRows(pending)); setEditing(true); }}
                data-testid={pending.length ? "pickup-change-btn" : "pickup-add-btn"}
              >
                {pending.length ? <><Pencil className="h-3.5 w-3.5 mr-1" /> Edit pickups</> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add pickup</>}
              </Button>
            )}
          </div>

          <div className="mt-1 space-y-2">
            {pickups.length ? (
              pickups.map((p, i) => (
                <PickupLine key={p.pharmacy_id} p={p} idx={i} canRemove={!isCompleted && !editing} onRemove={remove} busy={busy} />
              ))
            ) : (
              <div className="text-sm text-muted-foreground">No pickup stop on this route.</div>
            )}
          </div>

          {editing && !isCompleted && (
            <div className="mt-3 space-y-2" data-testid="pickup-edit-form">
              <PickupRows
                rows={rows}
                onChange={setRows}
                pharmacies={pharmacies}
                excludeIds={[startPharmacyId, ...lockedIds].filter(Boolean)}
                max={slots}
                testPrefix="pickup-edit"
              />
              <div className="flex gap-2">
                <Button onClick={save} disabled={busy || !rows.some((r) => r.pharmacy_id)} className="rounded-full" data-testid="pickup-edit-save">
                  <Check className="h-4 w-4 mr-1" /> {busy ? "Saving…" : "Save"}
                </Button>
                <Button onClick={() => setEditing(false)} variant="ghost" disabled={busy} className="rounded-full" data-testid="pickup-edit-cancel">
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          )}

          {isCompleted && (
            <p className="text-[11px] text-muted-foreground mt-2 italic">This route is completed — pickups are read-only.</p>
          )}
        </div>
      </div>
    </Card>
  );
}
